// One result per input, for every component at once (no React). The data hooks run in many components at the same
// time (every row of a table can call `useDerivedStats()`); a per-component `useMemo` would build one DataList and
// one derived result per component on every change. These selectors compute once per distinct inputs instead, and
// every caller gets the same object, so results are stable across components as well as across renders.

interface Node<R> {
  weak?: WeakMap<object, Node<R>>;
  strong?: Map<unknown, Node<R>>;
  done?: boolean;
  value?: R;
}

/**
 * Memoises `fn` on the identity of all its arguments, shared by every caller. Object arguments are held weakly, so an
 * entry goes away with the list it was computed from; primitives are kept in a Map under the objects before them, so
 * put at least one object (a list) before any primitive whose values can vary without bound.
 */
export function sharedMemo<A extends readonly unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const root: Node<R> = {};
  return (...args: A): R => {
    let node = root;
    for (const arg of args) {
      let next: Node<R> | undefined;
      if ((typeof arg === 'object' && arg !== null) || typeof arg === 'function') {
        node.weak ??= new WeakMap();
        next = node.weak.get(arg);
        if (!next) {
          next = {};
          node.weak.set(arg, next);
        }
      } else {
        node.strong ??= new Map();
        next = node.strong.get(arg);
        if (!next) {
          next = {};
          node.strong.set(arg, next);
        }
      }
      node = next;
    }
    if (!node.done) {
      node.value = fn(...args);
      node.done = true;
    }
    return node.value as R;
  };
}
